import React, { useState, useEffect } from "react";
import AccountingChart from "../components/AccountingChart";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Card,
  ButtonGroup,
  Form,
} from "react-bootstrap";
import api from "../api";

/**
 * 🌎 ThornsDashboard – All Platforms Overview
 * ---------------------------------------------------------
 * - Combined totals across ALL platforms
 * - Uses backend: /accounting/all/overview
 * - Same UI style as other pages (cards + chart)
 * - No table (high-level view only)
 */
function AccountingAll() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [period, setPeriod] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [summary, setSummary] = useState({
    totalSales: 0,
    totalCost: 0,
    totalFees: 0,
    totalShipping: 0,
    totalProfit: 0,
    profitMargin: 0,
  });

  /* ------------------------------------------------------------------------ */
  /* 🌎 Fetch ALL platform data                                               */
  /* ------------------------------------------------------------------------ */
  async function fetchAll(
    periodArg = period,
    startArg = startDate,
    endArg = endDate
  ) {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        period: periodArg || "",
        start: startArg || "",
        end: endArg || "",
      }).toString();

      const res = await api.get(`/accounting/all/overview?${query}`);

      const s = res.data.summary || {};

      setSummary({
        totalSales: parseFloat(s.total_sales || 0),
        totalCost: parseFloat(s.total_cost || 0),
        totalFees:
          parseFloat(s.platform_fees || 0) +
          parseFloat(s.promotion_fees || 0),
        totalShipping: parseFloat(s.shipping_cost || 0),
        totalProfit: parseFloat(s.total_profit || 0),
        profitMargin: parseFloat(s.profit_margin || 0),
      });

      setChartData(res.data.chart || []);
    } catch (err) {
      console.error("❌ Error fetching ALL platform data:", err);
    } finally {
      setLoading(false);
    }
  }

  /* ------------------------------------------------------------------------ */
  /* 🔄 Load on mount + refetch                                               */
  /* ------------------------------------------------------------------------ */
  useEffect(() => {
    fetchAll(period, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate]);

  /* ------------------------------------------------------------------------ */
  /* 🖼️ Render                                                               */
  /* ------------------------------------------------------------------------ */
  return (
    <Container fluid className="p-4">
      <h3 className="mb-3">🌎 All Platforms Dashboard</h3>

      {/* FILTERS */}
      <Row className="mb-3 align-items-center">
        <Col md={6}>
          <ButtonGroup>
            <Button
              variant={period === "day" ? "primary" : "outline-primary"}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setPeriod("day");
              }}
            >
              Today
            </Button>

            <Button
              variant={period === "week" ? "primary" : "outline-primary"}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setPeriod("week");
              }}
            >
              This Week
            </Button>

            <Button
              variant={period === "month" ? "primary" : "outline-primary"}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setPeriod("month");
              }}
            >
              This Month
            </Button>

            <Button
              variant={period === "" ? "primary" : "outline-secondary"}
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setPeriod("");
              }}
            >
              All Time
            </Button>

            <Button
              variant={period === "custom" ? "primary" : "outline-secondary"}
              onClick={() => setPeriod("custom")}
            >
              Custom
            </Button>
          </ButtonGroup>
        </Col>

        {period === "custom" && (
          <>
            <Col md={2}>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Col>
          </>
        )}

        <Col md={2}>
          <Button
            onClick={() => fetchAll(period, startDate, endDate)}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : "Apply"}
          </Button>
        </Col>
      </Row>

      {/* SUMMARY CARDS */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-success">
            <h5>Total Sales</h5>
            <h4>${summary.totalSales.toFixed(2)}</h4>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-warning">
            <h5>Total COGS</h5>
            <h4>${summary.totalCost.toFixed(2)}</h4>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-info">
            <h5>Total Shipping</h5>
            <h4>${summary.totalShipping.toFixed(2)}</h4>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-danger">
            <h5>Total Fees</h5>
            <h4>${summary.totalFees.toFixed(2)}</h4>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-primary">
            <h5>Net Profit</h5>
            <h4>
              ${summary.totalProfit.toFixed(2)} ({summary.profitMargin}%)
            </h4>
          </Card>
        </Col>

        <Col md={2}>
          <Card className="text-center p-3 shadow-sm border-dark">
            <h5>ROI</h5>
            <h4>
              {summary.totalCost > 0
                ? ((summary.totalProfit / summary.totalCost) * 100).toFixed(2)
                : "0.00"}
              %
            </h4>
          </Card>
        </Col>
      </Row>

      {/* CHART */}
      <h5 className="mb-3">📈 All Platform Sales vs Net Profit</h5>
      <AccountingChart chartData={chartData} period={period} />
    </Container>
  );
}

export default AccountingAll;